import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Input from "../components/Input";
import { Button } from "../components/Button";
import CalculatorLayout from "../components/CalcLayout";
import {
  setDownPayment,
  setMonths,
  setMonthlyPayment,
  setSecurityDeposit,
  setPurchasePrice,
  setInterestRate,
  setCarCalculationResults,
  setCarErrors,
  resetCarData,
} from "../redux/calculations/carSlice";
import {
  resetChartData,
  updatedCarChart,
} from "../redux/calculations/calculaterSlice";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const CarCalc = () => {
  const [result, setResult] = useState(null);
  const [calcDone, setCalcDone] = useState(false);

  const [exceedLimit, setExceedLimit] = useState("");
  const { textData } = useLocale();
  const { carCalc } = textData;
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const {
    downPayment,
    months,
    monthlyPayment,
    securityDeposit,
    purchasePrice,
    interestRate,
    carErrors,
  } = useSelector((state) => state.carCalculator);

  const formik = useFormik({
    initialValues: {
      downPayment: downPayment || 10000,
      months: months || 36,
      monthlyPayment: monthlyPayment || 10000,
      securityDeposit: securityDeposit || 10000,
      purchasePrice: purchasePrice || 500000,
      interestRate: interestRate || 12,
    },
    validationSchema: Yup.object({
      downPayment: Yup.number()
        .min(10000, carCalc.downPayment)
        .max(2000000, carCalc.downPayment)
        .required(carCalc.downPaymentRequired),
      months: Yup.number()
        .min(12, carCalc.months)
        .max(84, carCalc.months)
        .required(carCalc.monthsRequired),
      monthlyPayment: Yup.number()
        .min(1000, carCalc.monthlyPayment)
        .max(200000, carCalc.monthlyPayment)
        .required(carCalc.monthlyPaymentRequired),
      securityDeposit: Yup.number()
        .min(1000, carCalc.securityDeposit)
        .max(200000, carCalc.securityDeposit)
        .required(carCalc.securityDepositRequired),
      purchasePrice: Yup.number()
        .min(50000, carCalc.purchasePrice)
        .max(20000000, carCalc.purchasePrice)
        .required(carCalc.purchasePriceRequired),
      interestRate: Yup.number()
        .min(1, carCalc.interestRate)
        .max(15, carCalc.interestRate)
        .required(carCalc.interestRateRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_CARCALC");
      }

      const downPayment = parseFloat(values.downPayment);
      const months = parseInt(values.months);
      const monthlyPayment = parseFloat(values.monthlyPayment);
      const securityDeposit = parseFloat(values.securityDeposit);
      const purchasePrice = parseFloat(values.purchasePrice);
      const interestRate = parseFloat(values.interestRate) / 100;
      setExceedLimit(false);
      dispatch(setCarErrors(null));
      if (parseFloat(values.downPayment) >= parseFloat(values.purchasePrice)) {
        setExceedLimit(true);
        dispatch(setCarErrors(textData.carCalc.valueLimitError));

        setTimeout(() => {
          dispatch(setCarErrors(null));
          setExceedLimit(false);
        }, 2000);

        return;
      }

      const loanAmount = purchasePrice - downPayment;
      const monthlyRate = interestRate / 12;

      const loanPayment =
        (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
        (Math.pow(1 + monthlyRate, months) - 1);

      const totalInterest = loanPayment * months - loanAmount;
      const totalInterestPerMonth = loanPayment * months - loanAmount;

      const totalBuyCost =
        purchasePrice + totalInterest - downPayment + downPayment;

      const totalLeasePayments = monthlyPayment * months;

      const totalLeaseCost = totalLeasePayments + securityDeposit + downPayment;

      const calculationResult = {
        carBuyCost: Math.round(totalBuyCost),
        carLeaseCost: Math.round(totalLeaseCost),
        purchasePrice: Math.round(purchasePrice),
        monthlyBuyPayment: Math.round(loanPayment),
        totalInterest: Math.round(totalInterestPerMonth),
        totalLeasePayments: Math.round(totalLeasePayments),
        securityDeposit: Math.round(securityDeposit),
        downPayment: Math.round(downPayment),
      };

      setResult(calculationResult);
      dispatch(setDownPayment(values.downPayment));
      dispatch(setMonths(values.months));
      dispatch(setMonthlyPayment(values.monthlyPayment));
      dispatch(setSecurityDeposit(values.securityDeposit));
      dispatch(setPurchasePrice(values.purchasePrice));
      dispatch(setInterestRate(values.interestRate));
      dispatch(
        setCarCalculationResults({
          carBuyCost: calculationResult.carBuyCost,
          carLeaseCost: calculationResult.carLeaseCost,
          purchasePrice: calculationResult.purchasePrice,
          monthlyBuyPayment: calculationResult.monthlyBuyPayment,
          totalInterest: calculationResult.totalInterestPerMonth,
          totalLeasePayments: calculationResult.totalLeasePayments,
          securityDeposit: calculationResult.securityDeposit,
          downPayment: calculationResult.downPayment,
        })
      );

      dispatch(
        updatedCarChart({
          carBuyCost: calculationResult.carBuyCost,
          carLeaseCost: calculationResult.carLeaseCost,
          purchasePrice: calculationResult.purchasePrice,
          monthlyBuyPayment: calculationResult.monthlyBuyPayment,
          totalInterest: calculationResult.totalInterest,
          totalLeasePayments: calculationResult.totalLeasePayments,
          securityDeposit: calculationResult.securityDeposit,
          downPayment: calculationResult.downPayment,
        })
      );
      setCalcDone(true);
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetCarData());
    };
  }, [dispatch]);

  const downPaymentSliderRef = useRef(null);
  const monthsSliderRef = useRef(null);
  const monthlyPaymentSliderRef = useRef(null);
  const securityDepositSliderRef = useRef(null);
  const purchasePriceSliderRef = useRef(null);
  const interestRateSliderRef = useRef(null);

  const handleDownPaymentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setDownPayment(value));
    formik.setFieldValue("downPayment", value);
    setCalcDone(false);
  };

  const handleMonthsSliderChange = (e) => {
    const value = parseInt(e.target.value, 10);
    dispatch(setMonths(value));
    formik.setFieldValue("months", value);
    setCalcDone(false);
  };

  const handleMonthlyPaymentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setMonthlyPayment(value));
    formik.setFieldValue("monthlyPayment", value);
    setCalcDone(false);
  };

  const handleSecurityDepositSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setSecurityDeposit(value));
    formik.setFieldValue("securityDeposit", value);
    setCalcDone(false);
  };

  const handlePurchasePriceSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setPurchasePrice(value));
    formik.setFieldValue("purchasePrice", value);
    setCalcDone(false);
  };

  const handleInterestRateSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setInterestRate(value));
    formik.setFieldValue("interestRate", value);
    setCalcDone(false);
  };

  // Helper functions for formatting and parsing numbers
  const parseNumericInput = (value) => {
    return value.toString().replace(/[^0-9]/g, "");
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };
  // Handler for currency input fields
  const handleCurrencyInputChange = (e, fieldName) => {
    let numericValue = parseNumericInput(e.target.value);

    if (fieldName === "downPayment" && numericValue.length > 6) {
      numericValue = numericValue.slice(0, 8);
    } else if (fieldName === "monthlyPayment" && numericValue.length > 7) {
      numericValue = numericValue.slice(0, 8);
    } else if (fieldName === "securityDeposit" && numericValue.length > 7) {
      numericValue = numericValue.slice(0, 8);
    } else if (fieldName === "purchasePrice" && numericValue.length > 8) {
      numericValue = numericValue.slice(0, 8);
    }

    const numberValue = numericValue === "" ? "" : Number(numericValue);

    formik.setFieldValue(fieldName, numberValue);

    // Update Redux state if needed
    switch (fieldName) {
      case "downPayment":
        dispatch(setDownPayment(numberValue));
        break;
      case "monthlyPayment":
        dispatch(setMonthlyPayment(numberValue));
        break;
      case "securityDeposit":
        dispatch(setSecurityDeposit(numberValue));
        break;
      case "purchasePrice":
        dispatch(setPurchasePrice(numberValue));
        break;
      default:
        break;
    }
  };

  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("interestRate", rawValue);
      dispatch(setInterestRate(""));
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;

    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    formik.setFieldValue("interestRate", normalizedValue);
    dispatch(setInterestRate(normalizedValue));
    setCalcDone(false);
  };

  useEffect(() => {
    // Ensure Redux state is updated when the form values change
    if (formik.values.downPayment) {
      dispatch(setDownPayment(formik.values.downPayment));
    }
  }, [formik.values.downPayment, dispatch]);

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.carLeaseContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.carLeaseContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.carLeaseContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.carLeaseContent.howItWorksDescription}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        <strong>{textData.carLeaseContent.emiFormulaLabel}</strong>
        <br />
        {textData.carLeaseContent.emiFormula}
      </p>

      <table className="border-collapse mt-4 w-full">
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              p
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.carLeaseContent.symbolTable.p}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              r
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.carLeaseContent.symbolTable.r}
            </td>
          </tr>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              n
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.carLeaseContent.symbolTable.n}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.carLeaseContent.upfrontCostsTitle}
      </h2>

      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        <strong>{textData.carLeaseContent.buy}</strong>{" "}
        {textData.carLeaseContent.upfrontCostsDescription.buying}
      </p>
      <p className="text-sm sm:text-[15px]">
        {" "}
        <strong>{textData.carLeaseContent.lease}</strong>{" "}
        {textData.carLeaseContent.upfrontCostsDescription.leasing}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.carLeaseContent.additionalExpensesTitle}
      </h2>

      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong>{textData.carLeaseContent.maintenance}</strong>{" "}
          {textData.carLeaseContent.additionalExpenses.maintenance}
        </li>
        <li>
          <strong>{textData.carLeaseContent.mileage}</strong>
          {textData.carLeaseContent.additionalExpenses.mileageLimits}
        </li>
        <li>
          <strong>{textData.carLeaseContent.onPay}</strong>
          {textData.carLeaseContent.additionalExpenses.ongoingPayments}
        </li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.carLeaseContent.whyBuyingCostsMoreTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.carLeaseContent.whyBuyingCostsMoreDescription}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.carLeaseContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.carLeaseContent.benefitsList[0]}</li>
        <li>{textData.carLeaseContent.benefitsList[1]} </li>
        <li> {textData.carLeaseContent.benefitsList[2]}</li>
      </ul>
    </div>
  );

  const timeLabel = t("month").charAt(0).toUpperCase();

  const getFormattedPlaceholder = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  return (
    <>
      <Helmet>
        <title>Car Lease vs Buy Calculator – Save on your next car</title>
        <meta
          name="description"
          content="Compare lease car vs buy car calculator with our free finance calculator. Instantly see potential savings and decide the best option for your next vehicle."
        />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/car-lease-calculator"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Car Lease vs Buy Calculator – Save on your next car"
        />
        <meta
          property="og:description"
          content="Compare lease car vs buy car calculator with our free finance calculator. Instantly see potential savings and decide the best option for your next vehicle."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/car-lease-calculator"
        />
        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />
        <meta
          property="og:image:alt"
          content="Calculate Savings with Car Lease vs Buy Calculator"
        />

        {/* Robots */}
        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        generateData={result}
        title={
          <>
            {" "}
            {textData.carCalc.title}
            <span className="pl-2 text-black lg:text-xl sm:text-lg text-lg">
              {textData.carCalc.Costs}
            </span>
          </>
        }
        calcDone={calcDone}
        description={textData.carCalc.description}
        descriptionContent={descriptionContent}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">

            {/* Down Payment */}
            <div>
              <h3 className="font-bold text-start text-black  text-base sm:text-base mb-4 sm:mb-4">
                {textData.carCalc.commonAssumptions}
              </h3>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.carCalc.downPaymentLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={downPaymentSliderRef}>
                  <input
                    type="range"
                    min={10000}
                    max={2000000}
                    step={1000}
                    value={formik.values.downPayment}
                    onChange={handleDownPaymentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.downPayment - 10000) /
                        (2000000 - 10000)) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="downPayment"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.downPayment)}
                    onChange={(e) =>
                      handleCurrencyInputChange(e, "downPayment")
                    }
                    dir="ltr"
                    onBlur={formik.handleBlur}
                    type="text"
                    placeholder={getFormattedPlaceholder(10000, i18n.language)}
                    borderColor={
                      formik.touched.downPayment && formik.errors.downPayment
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]    border ${formik.touched.downPayment && formik.errors.downPayment
                      ? "border-danger"
                      : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.downPayment && formik.errors.downPayment && (
                    <div className="text-danger text-[12px] text-center sm:text-sm mt-1">
                      {formik.errors.downPayment}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* No. of Months */}
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.carCalc.monthsLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={monthsSliderRef}>

                  <input
                    type="range"
                    min={12}
                    max={84}
                    step={1}
                    value={formik.values.months}
                    onChange={handleMonthsSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.months - 12) / (84 - 12)) * 100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col">
                  <Input
                    name="months"
                    leftIcon={timeLabel}
                    value={formik.values.months}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,2}$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"36"}
                    borderColor={
                      formik.touched.months && formik.errors.months
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]  border ${formik.touched.months && formik.errors.months
                      ? "border-danger"
                      : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.months && formik.errors.months && (
                    <div className="text-danger text-[12px] text-center sm:text-sm mt-1">
                      {formik.errors.months}
                    </div>
                  )}
                </div>
              </div>
            </div>


            <div>
              <h3 className="font-bold text-start text-black  text-base sm:text-base  mb-4 sm:mb-4">
                {textData.carCalc.leaseInformation}
              </h3>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.carCalc.monthlyPaymentLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={monthlyPaymentSliderRef}
                >

                  <input
                    type="range"
                    min={1000}
                    max={200000}
                    step={500}
                    value={formik.values.monthlyPayment}
                    onChange={handleMonthlyPaymentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.monthlyPayment - 1000) /
                        (200000 - 1000)) *
                        100
                        }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-end flex-col relative">
                  <Input
                    name="monthlyPayment"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.monthlyPayment)}
                    onChange={(e) =>
                      handleCurrencyInputChange(e, "monthlyPayment")
                    }
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(10000, i18n.language)}
                    borderColor={
                      formik.touched.monthlyPayment &&
                        formik.errors.monthlyPayment
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  border ${formik.touched.monthlyPayment &&
                      formik.errors.monthlyPayment
                      ? "border-danger"
                      : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  <span className=" hidden sm:flex absolute top-[7px] sm:top-[9px] right-[14px] text-gray-font text-sm   font-normal">
                    {textData.carCalc.perMonthSuffix}
                  </span>
                  {formik.touched.monthlyPayment &&
                    formik.errors.monthlyPayment && (
                      <div className="text-danger text-[12px] text-center sm:text-sm mt-1">
                        {formik.errors.monthlyPayment}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Security Deposit */}
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.carCalc.securityDepositLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={securityDepositSliderRef}
                >

                  <input
                    type="range"
                    min={1000}
                    max={200000}
                    step={1000}
                    value={formik.values.securityDeposit}
                    onChange={handleSecurityDepositSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.securityDeposit - 1000) /
                        (200000 - 1000)) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="securityDeposit"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.securityDeposit)}
                    onChange={(e) =>
                      handleCurrencyInputChange(e, "securityDeposit")
                    }
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(10000, i18n.language)}
                    borderColor={
                      formik.touched.securityDeposit &&
                        formik.errors.securityDeposit
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={`sm:h-[36px] h-[32px]  ${formik.touched.securityDeposit &&
                      formik.errors.securityDeposit
                      ? "border-danger"
                      : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.securityDeposit &&
                    formik.errors.securityDeposit && (
                      <div className="text-danger text-[12px] text-center sm:text-sm mt-1">
                        {formik.errors.securityDeposit}
                      </div>
                    )}
                </div>
              </div>{" "}
            </div>


            {/* Purchase Price */}
            <div>
              <h3 className="font-bold text-start text-black  text-base sm:text-base  mb-4 sm:mb-4">
                {textData.carCalc.purchaseInformation}
              </h3>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.carCalc.purchasePriceLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div
                  className="relative   w-full "
                  ref={purchasePriceSliderRef}
                >

                  <input
                    type="range"
                    min={50000}
                    max={20000000}
                    step={1000}
                    value={formik.values.purchasePrice}
                    onChange={handlePurchasePriceSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.purchasePrice - 50000) /
                        (20000000 - 50000)) *
                        100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="purchasePrice"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.purchasePrice)}
                    onChange={(e) =>
                      handleCurrencyInputChange(e, "purchasePrice")
                    }
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getFormattedPlaceholder(50000, i18n.language)}
                    borderColor={
                      formik.touched.purchasePrice &&
                        formik.errors.purchasePrice
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  ${formik.touched.purchasePrice &&
                      formik.errors.purchasePrice
                      ? "border-danger"
                      : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.purchasePrice &&
                    formik.errors.purchasePrice && (
                      <div className="text-danger text-[12px] text-center sm:text-sm mt-1">
                        {formik.errors.purchasePrice}
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Rate of Interest */}
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.carCalc.interestRateLabel}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={interestRateSliderRef}>

                  <input
                    type="range"
                    min={1}
                    max={15}
                    step={0.1}
                    value={parseFloat(formik.values.interestRate)}
                    onChange={handleInterestRateSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.interestRate - 1) / (15 - 1)) * 100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-end flex-col">
                  <Input
                    name="interestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("12.0")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.interestRate
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.interestRate && formik.errors.interestRate
                        ? "border-red-500"
                        : "border-gray-light"
                    }
                    className={` sm:h-[36px] h-[32px]  ${formik.touched.interestRate && formik.errors.interestRate
                      ? "border-danger"
                      : "border-gray-light"
                      } flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]`}
                  />
                  {formik.touched.interestRate &&
                    formik.errors.interestRate && (
                      <div className="text-danger text-[12px] text-center sm:text-sm mt-1">
                        {formik.errors.interestRate}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div className="mt-[10px] relative">
              <Button
                type="submit"
                className="w-full mb-2"
                disabled={exceedLimit}
              >
                {textData.calculate}
              </Button>
              {carErrors && (
                <div className=" absolute bottom-[-50] z-50 w-full text-red-500 mb-4 text-center px-2 py-2 sm:py-4  rounded-md text-black bg-gray-light border-none text-sm">
                  {carErrors}
                </div>
              )}
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default CarCalc;
